import { TranslationDto } from '../translation.dto'

import { MilestoneType } from './challenge-milestone.dto'

/**
 * Nguồn gốc cấp tem.
 * Khớp enum `challenge_stamp_source_type` (`ChallengeStamp.sourceType`).
 */
export const STAMP_SOURCE_TYPE = {
  PROFILE: 'PROFILE',
  QR: 'QR',
  FNB_ORDER: 'FNB_ORDER',
  MALL_ORDER: 'MALL_ORDER',
  REQUEST: 'REQUEST'
} as const

export type StampSourceType = typeof STAMP_SOURCE_TYPE[keyof typeof STAMP_SOURCE_TYPE]

/**
 * API #21/#22 — kết quả một lần cấp tem.
 *
 * ✅ ĐÃ CHỐT: mặc định (không truyền `resultStatus`) BE **chỉ trả `ISSUED`**, không có
 * trường hợp trả lẫn cả `ISSUED` và `FAILED` trong cùng 1 lần gọi. Muốn xem tem lỗi phải
 * gọi riêng với `resultStatus=FAILED`. Bảng Stamp History hiện tại **không hiển thị** tem
 * lỗi (đã bỏ hẳn khái niệm `failedCount` khỏi `StampHistorySummaryDto`) — nếu sau này cần,
 * gọi thêm 1 request riêng với `resultStatus=FAILED`.
 */
export const STAMP_RESULT_STATUS = {
  ISSUED: 'ISSUED',
  FAILED: 'FAILED'
} as const

export type StampResultStatus = typeof STAMP_RESULT_STATUS[keyof typeof STAMP_RESULT_STATUS]

/**
 * FR-06-BR-01 — tổng hợp số tem đã phát theo từng Milestone (BE aggregate).
 *
 * API #21 `GET /challenge-stamps/summary?challengeId&...`
 * (trước đây FE giả định `GET /challenges/:id/stamp-history/summary`).
 *
 * ⚠️ Summary giờ nhận **toàn bộ filter** của API #22 (`milestoneId`, `sourceType`,
 * `resultStatus`, `failureCode`, `search`, `from`, `to`) ⇒ không còn là tổng cố định
 * mà thay đổi theo filter đang chọn.
 */
export class StampHistorySummaryDto {
  /** ⚠️ BE trả tên `milestoneId`. */
  public challengeMilestoneId?: string
  /**
   * `ChallengeMilestone.position` — FE dựng mã MS-00x từ giá trị này.
   *
   * ✅ Swagger 2026-08-25: API #21 đã trả field này. Với Milestone chưa phát tem (không có
   * dòng trong #21) thì giá trị đến từ API #10 — xem `mergeSummaryWithMilestones()`.
   */
  public milestonePosition?: number
  /** ⚠️ BE trả `milestoneName: string` (đã localize) — service bọc thành TranslationDto. */
  public milestoneName?: TranslationDto
  /**
   * Quyết định dựng QR thế nào: `VISIT_MALL` → API #15 (tĩnh), `CHECK_IN_EVENT` → QR của
   * Event từ `eventId`, `MALL_SHOPPING` → API #16 (động 60s).
   *
   * ✅ Swagger 2026-08-25: API #21 đã trả field này; Milestone chưa phát tem lấy từ API #10
   * như `milestonePosition`.
   */
  public milestoneType?: MilestoneType
  /** Tổng tem đã phát. ⚠️ BE trả tên `stampCount`; không có dòng nghĩa là 0. */
  public issuedStamps?: number
  /**
   * Milestone có QR hay không (FR-06-UI-01) — mã QR thật phải gọi API #15/#16.
   *
   * ⚠️ Chỉ nói về QR **tĩnh** (BE xác nhận 2026-09-15) nên luôn `false`/rỗng với
   * `MALL_SHOPPING`. Điều kiện hiện nút View QR phải xét thêm `milestoneType` — xem
   * `StampHistoryComponent.hasQrCode()`.
   */
  public qrAvailable?: boolean
  /**
   * Customer FR-05-BR-05 — Milestone Check-in Event dùng chung QR của Event (mỗi Event 1 QR)
   * nên popup dựng QR từ `eventId`, không gọi API #15. API #21 không trả field này, lấy từ
   * API #10 qua `mergeSummaryWithMilestones()`.
   */
  public eventId?: string
  /** API #21 — mới, cho phép summary cross-challenge. */
  public challengeId?: string
  public challengeName?: string

  public static create(data?: Partial<StampHistorySummaryDto>): StampHistorySummaryDto {
    const instance = new StampHistorySummaryDto()
    if (data) {
      Object.assign(instance, data)
    }
    return instance
  }
}

/**
 * FR-06-BR-02 — một dòng của bảng `ChallengeStamp`.
 *
 * API #22 `GET /challenge-stamps` (trước đây FE giả định
 * `GET /challenges/:id/stamp-history`). BE trả 19 field.
 */
export class StampHistoryItemDto {
  /** ⚠️ BE trả tên `stampId`. */
  public id?: string
  /**
   * Mã tem hiển thị trên UI (cột "Stamp ID").
   *
   * ⚠️ BE-MISSING N5 (C37 vẫn mở): **không có** trong response API #22
   * ⇒ `stamp-history.component.html:149` sẽ trống.
   */
  public stampCode?: string
  /** ✅ ĐÃ CHỐT: `customerId` là `User.id` (không phải `ChallengeCustomer.id`). */
  public customerId?: string
  public customerName?: string
  /** ⚠️ BE trả tên `customerPhoneNumber`. */
  public phoneNumber?: string
  /** ⚠️ BE trả tên `milestoneId`. */
  public challengeMilestoneId?: string
  /**
   * `ChallengeMilestone.position` — FE dựng mã MS-00x từ giá trị này.
   * ⚠️ BE-MISSING: API #22 không trả — join từ API #10 (xem `StampHistorySummaryDto`).
   */
  public milestonePosition?: number
  /** ⚠️ BE trả `milestoneName: string` (đã localize). */
  public milestoneName?: TranslationDto
  /** `ChallengeStamp.sourceType` — nguồn cấp tem. ✅ khớp BE (5 giá trị). */
  public sourceType?: StampSourceType
  /** `ChallengeStamp.mallId` — null khi tem thoả mãn nhiều Mall (đơn online).
   *  ⚠️ BE trả `mallName: string` (đã localize). */
  public mallName?: TranslationDto
  /** `ChallengeStamp.offlineTenantId`. ⚠️ BE trả string đã localize. */
  public offlineTenantName?: TranslationDto
  /** Chỉ có khi tem phát từ giao dịch có mã đơn hàng. ✅ khớp BE. */
  public orderNumber?: string
  public transactionAmount?: number
  /** `ChallengeStamp.issuedAt`. ISO Date string. ✅ khớp BE — null khi FAILED. */
  public issuedAt?: string
  /** API #22 — mới. Chỉ có giá trị khác `ISSUED` khi chủ động truyền `resultStatus=FAILED`. */
  public resultStatus?: StampResultStatus
  /** API #22 — mới: mã lý do thất bại. ⚠️ Cần BE cung cấp danh sách để làm i18n. */
  public failureCode?: string
  /**
   * API #22 — mới: thời điểm **thử** cấp tem (tách khỏi `issuedAt`).
   * Với row FAILED thì chỉ có `attemptedAt`, `issuedAt = null`.
   */
  public attemptedAt?: string
  /** API #22 — mới, id thô kèm theo tên. */
  public mallId?: string
  public offlineTenantId?: string
  public challengeId?: string
  public challengeName?: string

  public static create(data?: Partial<StampHistoryItemDto>): StampHistoryItemDto {
    const instance = new StampHistoryItemDto()
    if (data) {
      Object.assign(instance, data)
    }
    return instance
  }
}

/**
 * FR-06-BR-03 — tham số tìm kiếm và lọc (API #21, #22).
 * ⚠️ `challengeId` giờ là **query param** (trước là path); `fromDate`/`toDate` map sang
 * `from`/`to`.
 */
export class FindStampHistoryDto {
  public challengeId?: string
  /** Tìm theo Customer name, Phone number hoặc Order number. ✅ khớp BE. */
  public search?: string
  public milestoneId?: string
  /** API #22 — mới. UI hiện chưa có control này. */
  public sourceType?: StampSourceType
  /**
   * API #22 — mới. ✅ ĐÃ CHỐT: bảng Stamp History chỉ hiển thị tem `ISSUED` (mặc định của
   * BE khi không truyền field này). UI không cần thêm control lọc FAILED.
   */
  public resultStatus?: StampResultStatus
  /** API #22 — mới. UI hiện chưa có control này. */
  public failureCode?: string
  /** ISO Date string → query `from`. */
  public fromDate?: string
  /** ISO Date string → query `to`. */
  public toDate?: string
  public page?: number
  public pageSize?: number

  public static create(data?: Partial<FindStampHistoryDto>): FindStampHistoryDto {
    const instance = new FindStampHistoryDto()
    if (data) {
      Object.assign(instance, data)
    }
    return instance
  }
}

/**
 * FR-06-BR-04 — payload export.
 *
 * ✅ ĐÃ CHỐT (probe HTTP xác nhận `POST /challenge-stamps/export-excel` → 404): BE **không
 * có** biến thể gửi email cho Stamp History (khác Reward History — có cả email lẫn tải trực
 * tiếp, giữ email làm mặc định vì dữ liệu Reward có thể lớn). Stamp History chỉ có API #23
 * (binary) ⇒ `AdminStampHistoryService.exportExcel()` uỷ quyền thẳng sang `downloadExcel()`,
 * màn Stamp History tải file trực tiếp thay vì gửi email.
 */
export class ExportStampHistoryDto {
  public challengeId?: string
  public email?: string
  /** → query/body `from`. */
  public startDate?: Date
  /** → query/body `to`. */
  public endDate?: Date
  public milestoneId?: string
  public search?: string
  /** API #23 — filter mới, giữ parity với `FindStampHistoryDto`. */
  public sourceType?: StampSourceType
  public resultStatus?: StampResultStatus
  public failureCode?: string

  public static create(data?: Partial<ExportStampHistoryDto>): ExportStampHistoryDto {
    const instance = new ExportStampHistoryDto()
    if (data) {
      Object.assign(instance, data)
    }
    return instance
  }
}
